import { z } from "zod";
import type { FieldPath, FieldPathKind } from "../sync/field-path.js";
import type { Operation } from "../sync/operation.js";
import type { OperationLog } from "../sync/operation-log.js";
import type { SnapshotMutation } from "../sync/snapshot-mutation.js";
import { CodecError, instantSchema } from "./envelope.js";
import {
  decodeLogicalTimestamp,
  encodeLogicalTimestamp,
  logicalTimestampJsonSchema,
} from "./logical-timestamp.js";
import {
  decodeSnapshotMutation,
  encodeSnapshotMutation,
  snapshotMutationJsonSchema,
  type SnapshotMutationJson,
} from "./snapshot-mutation.js";

/**
 * Phase 9a codec for the offline-first op-log. Wire-shape pivots on
 * the `fieldPath.kind` discriminant, which both identifies the
 * target field and selects the `value` schema. Determinism rules
 * mirror Phase 6a: declared key order on every emitted object,
 * canonical millisecond-precision ISO 8601 for `issuedAt`,
 * `operations` array preserved in insertion order (Phase 9's
 * sortedOperations / reconciler is a *consumer-side* projection;
 * the persisted log is insertion-ordered).
 *
 * The codec validates *only structural* invariants: numeric ranges,
 * string non-emptiness, enum membership. Cross-aggregate invariants
 * (the field's target aggregate exists in the graph) are re-checked
 * at apply time by `applyOperation` and surface as
 * `ApplyOperationError` rather than `CodecError`.
 */

// LogicalTimestamp codec moved to `./logical-timestamp.ts` so it can
// be shared between this file and `./snapshot-mutation.ts` without a
// circular import. Re-exported below for backward compatibility with
// existing callers.
export {
  decodeLogicalTimestamp,
  encodeLogicalTimestamp,
  logicalTimestampJsonSchema,
};

const circuitStatusSchema = z.enum(["in-service", "spare", "disconnected"]);
const wireMaterialSchema = z.enum(["copper", "aluminum", "copper-clad-aluminum"]);

export const fieldPathJsonSchema: z.ZodType<FieldPath> = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("circuit-canonical-description"),
    circuitId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("circuit-amperage"),
    circuitId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("circuit-legend-override"),
    circuitId: z.string().min(1),
  }),
  z.object({ kind: z.literal("circuit-status"), circuitId: z.string().min(1) }),
  z.object({
    kind: z.literal("circuit-wire-gauge"),
    circuitId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("circuit-wire-material"),
    circuitId: z.string().min(1),
  }),
  z.object({ kind: z.literal("circuit-tags"), circuitId: z.string().min(1) }),
  z.object({ kind: z.literal("device-name"), deviceId: z.string().min(1) }),
  z.object({
    kind: z.literal("device-location-within-room"),
    deviceId: z.string().min(1),
  }),
  z.object({ kind: z.literal("panel-manufacturer"), panelId: z.string().min(1) }),
  z.object({ kind: z.literal("panel-model"), panelId: z.string().min(1) }),
  z.object({
    kind: z.literal("panel-main-amperage"),
    panelId: z.string().min(1),
  }),
  z.object({ kind: z.literal("site-name"), siteId: z.string().min(1) }),
  z.object({ kind: z.literal("room-name"), roomId: z.string().min(1) }),
  z.object({
    kind: z.literal("warning-dismiss"),
    warningId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("warning-undismiss"),
    warningId: z.string().min(1),
  }),
]);

export function encodeFieldPath(path: FieldPath): Record<string, unknown> {
  switch (path.kind) {
    case "circuit-canonical-description":
    case "circuit-amperage":
    case "circuit-legend-override":
    case "circuit-status":
    case "circuit-wire-gauge":
    case "circuit-wire-material":
    case "circuit-tags":
      return { kind: path.kind, circuitId: path.circuitId };
    case "device-name":
    case "device-location-within-room":
      return { kind: path.kind, deviceId: path.deviceId };
    case "panel-manufacturer":
    case "panel-model":
    case "panel-main-amperage":
      return { kind: path.kind, panelId: path.panelId };
    case "site-name":
      return { kind: path.kind, siteId: path.siteId };
    case "room-name":
      return { kind: path.kind, roomId: path.roomId };
    case "warning-dismiss":
    case "warning-undismiss":
      return { kind: path.kind, warningId: path.warningId };
  }
}

export function decodeFieldPath(input: unknown): FieldPath {
  const parsed = fieldPathJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeFieldPath: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

/**
 * Per-kind value schema. Kept in one map so adding a `FieldPathKind`
 * adds exactly one entry — the same one-line pattern Phase 8b's
 * `PROPOSAL_SCHEMAS` registry uses for AI proposals.
 */
const VALUE_SCHEMAS = {
  "circuit-canonical-description": z.string(),
  // Required positive integer — a circuit always has a rating
  // (Phase 15a); no `null` "cleared" state for this arm.
  "circuit-amperage": z.number().int().positive(),
  "circuit-legend-override": z.union([z.string(), z.null()]),
  "circuit-status": circuitStatusSchema,
  // `WireGauge` is a free-form string at the domain layer
  // (`circuit/wire.ts`); the codec mirrors that — empty string is
  // legal-but-unusual (same convention `panel-manufacturer` uses)
  // and the Phase 3 amp/wire-gauge mismatch validator advises
  // downstream rather than rejecting at the codec. `null` is the
  // canonical "cleared" representation.
  "circuit-wire-gauge": z.union([z.string(), z.null()]),
  "circuit-wire-material": z.union([wireMaterialSchema, z.null()]),
  // Whole-array LWW per Phase 9d. Empty array (`[]`) is the
  // "no tags" representation; `null` is rejected to keep one
  // canonical empty shape.
  "circuit-tags": z.array(z.string()),
  "device-name": z.string(),
  "device-location-within-room": z.union([z.string(), z.null()]),
  "panel-manufacturer": z.union([z.string(), z.null()]),
  "panel-model": z.union([z.string(), z.null()]),
  "panel-main-amperage": z.union([z.number().int().positive(), z.null()]),
  "site-name": z.string(),
  "room-name": z.string(),
  // Dismissal note: `string` (the note, possibly empty) or `null`
  // (dismissed without a note). Mirrors `Warning.dismissalNote`'s
  // `string | undefined` shape; the codec uses `null` instead of
  // `undefined` so the JSON wire shape stays explicit.
  "warning-dismiss": z.union([z.string(), z.null()]),
  // The undismiss op carries no payload — the kind alone flips
  // status back to `open`. `null` is the only legal wire value;
  // any other value is rejected so the round-trip shape stays
  // unambiguous and a Phase 12 client cannot accidentally smuggle
  // a string note into an undismiss op.
  "warning-undismiss": z.null(),
} as const satisfies Record<FieldPathKind, z.ZodTypeAny>;

const baseOperationFrameSchema = z.object({
  opId: z.string().min(1),
  timestamp: logicalTimestampJsonSchema,
  issuedAt: instantSchema,
  fieldPath: fieldPathJsonSchema,
  value: z.unknown(),
});

export const operationJsonSchema = baseOperationFrameSchema.superRefine(
  (op, ctx) => {
    const valueSchema = VALUE_SCHEMAS[op.fieldPath.kind];
    const parsed = valueSchema.safeParse(op.value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value", ...issue.path],
          message: `Operation value invalid for fieldPath.kind "${op.fieldPath.kind}": ${issue.message}`,
        });
      }
    }
  },
);

export type OperationJson = {
  readonly opId: string;
  readonly timestamp: { readonly logical: number; readonly deviceId: string };
  readonly issuedAt: string;
  readonly fieldPath: FieldPath;
  readonly value: unknown;
};

export function encodeOperation(op: Operation): Record<string, unknown> {
  return {
    opId: op.opId,
    timestamp: encodeLogicalTimestamp(op.timestamp),
    issuedAt: op.issuedAt,
    fieldPath: encodeFieldPath(op.fieldPath),
    value: op.value,
  };
}

export function decodeOperation(input: unknown): Operation {
  const parsed = operationJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeOperation: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  // The superRefine above validated `value` against the per-kind
  // schema, so the `(fieldPath.kind, value)` pair is internally
  // consistent. TypeScript's union-narrowing can't see across the
  // refinement, so we cast the constructed shape to `Operation`.
  return {
    opId: j.opId,
    timestamp: j.timestamp,
    issuedAt: j.issuedAt,
    fieldPath: j.fieldPath,
    value: j.value,
  } as Operation;
}

export const operationLogJsonSchema = z.object({
  operations: z.array(operationJsonSchema),
  mutations: z.array(snapshotMutationJsonSchema).optional(),
});

export type OperationLogJson = {
  readonly operations: ReadonlyArray<OperationJson>;
  readonly mutations?: ReadonlyArray<SnapshotMutationJson>;
};

/**
 * Phase 9f3 — the codec round-trips both `operations` and
 * `mutations`. The wire object key order is `operations` first, then
 * `mutations`; an empty `mutations` array is emitted explicitly so
 * the wire shape is unambiguous (decoders can distinguish "no
 * mutations" from "older wire shape that predates 9f3").
 *
 * Backward-decode: a pre-9f3 wire shape lacks the `mutations` key
 * entirely. The schema accepts that via the `.optional()` modifier
 * and the decoder fills `mutations: []`. Forward-only producers
 * (this `encodeOperationLog`) always emit the key, so a recent
 * client's output is always self-describing.
 */
export function encodeOperationLog(log: OperationLog): Record<string, unknown> {
  return {
    operations: log.operations.map(encodeOperation),
    mutations: log.mutations.map(encodeSnapshotMutation),
  };
}

export function decodeOperationLog(input: unknown): OperationLog {
  const parsed = operationLogJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeOperationLog: ${parsed.error.message}`,
    );
  }
  const operations = parsed.data.operations.map((raw) => decodeOperation(raw));
  const mutations: SnapshotMutation[] =
    parsed.data.mutations === undefined
      ? []
      : parsed.data.mutations.map((raw) => decodeSnapshotMutation(raw));
  return { operations, mutations };
}
